import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Pencil, Trash2, Star, Eye, EyeOff, Save, Loader2, X, Quote } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Switch } from '../../components/ui/switch';
import { AdminNav } from './AdminDashboard';
import { useAuth } from '../../lib/auth';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import API from '../../lib/api';

const inputCls = 'bg-[#232323] border-white/[0.10] rounded-lg h-10 text-sm text-white placeholder:text-white/25 focus:border-gold focus:ring-1 focus:ring-gold/20';
const labelCls = 'text-[10px] uppercase tracking-widest text-white/35 mb-1 block';

function TestimonialForm({ testimonial, onSave, onCancel }) {
  const isEdit = !!testimonial?.id;
  const [form, setForm] = useState({
    quote: testimonial?.quote || '',
    author: testimonial?.author || '',
    role: testimonial?.role || '',
    rating: testimonial?.rating || 5,
    is_visible: testimonial?.is_visible ?? true,
    display_order: testimonial?.display_order || 0,
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.quote.trim()) { toast.error('Quote is required'); return; }
    if (!form.author.trim()) { toast.error('Author name is required'); return; }
    setSaving(true);
    try {
      if (isEdit) {
        await API.put(`/admin/testimonials/${testimonial.id}`, form);
        toast.success('Testimonial updated');
      } else {
        await API.post('/admin/testimonials', form);
        toast.success('Testimonial created');
      }
      onSave();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save'); }
    setSaving(false);
  };

  return (
    <motion.form
      initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
      onSubmit={handleSubmit}
      className="bg-[#1c1c1c] border border-white/[0.06] rounded-xl p-6 space-y-4"
      data-testid="testimonial-form"
    >
      <div className="flex items-center justify-between mb-2">
        <h3 className="font-heading text-lg text-white font-semibold">{isEdit ? 'Edit Testimonial' : 'New Testimonial'}</h3>
        <button type="button" onClick={onCancel} className="text-white/30 hover:text-white"><X className="w-5 h-5" /></button>
      </div>
      <div>
        <label className={labelCls}>Quote *</label>
        <textarea
          value={form.quote} onChange={e => setForm(f => ({ ...f, quote: e.target.value }))}
          rows={3} required placeholder="What the guest said..."
          className="w-full bg-[#232323] border border-white/[0.10] rounded-lg p-3 text-sm text-white resize-none focus:outline-none focus:border-gold focus:ring-1 focus:ring-gold/20 placeholder:text-white/25"
          data-testid="testimonial-quote"
        />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className={labelCls}>Author Name *</label>
          <Input value={form.author} onChange={e => setForm(f => ({ ...f, author: e.target.value }))} required placeholder="e.g. Sarah M." className={inputCls} data-testid="testimonial-author" />
        </div>
        <div>
          <label className={labelCls}>Role / Origin</label>
          <Input value={form.role} onChange={e => setForm(f => ({ ...f, role: e.target.value }))} placeholder="e.g. Guest from Toronto" className={inputCls} data-testid="testimonial-role" />
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div>
          <label className={labelCls}>Rating (1-5)</label>
          <div className="flex gap-1 mt-1">
            {[1, 2, 3, 4, 5].map(n => (
              <button key={n} type="button" onClick={() => setForm(f => ({ ...f, rating: n }))}
                className="p-1" data-testid={`rating-star-${n}`}>
                <Star className={`w-5 h-5 ${n <= form.rating ? 'fill-gold text-gold' : 'text-white/15'}`} />
              </button>
            ))}
          </div>
        </div>
        <div>
          <label className={labelCls}>Display Order</label>
          <Input type="number" value={form.display_order} onChange={e => setForm(f => ({ ...f, display_order: parseInt(e.target.value) || 0 }))} className={`${inputCls} w-24`} />
        </div>
        <div className="flex items-center gap-3 pt-4">
          <Switch checked={form.is_visible} onCheckedChange={v => setForm(f => ({ ...f, is_visible: v }))} data-testid="testimonial-visible" />
          <span className="text-sm text-white/50">Visible on site</span>
        </div>
      </div>
      <div className="flex gap-3 pt-2">
        <Button type="submit" disabled={saving} className="bg-gold text-white hover:bg-[#9A7B1F] rounded-lg text-xs uppercase tracking-widest gap-2" data-testid="save-testimonial-btn">
          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} {isEdit ? 'Update' : 'Create'}
        </Button>
        <Button type="button" onClick={onCancel} variant="outline" className="border-white/[0.10] text-white/40 rounded-lg text-xs uppercase tracking-widest">Cancel</Button>
      </div>
    </motion.form>
  );
}

export default function AdminTestimonials() {
  const { isAdmin, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [deleting, setDeleting] = useState(null);

  useEffect(() => { if (!authLoading && !isAdmin) navigate('/login'); }, [isAdmin, authLoading, navigate]);

  const fetchTestimonials = () => {
    API.get('/admin/testimonials').then(r => { setTestimonials(r.data.testimonials); setLoading(false); }).catch(() => setLoading(false));
  };
  useEffect(fetchTestimonials, []);

  const handleDelete = async (id) => {
    setDeleting(id);
    try {
      await API.delete(`/admin/testimonials/${id}`);
      toast.success('Testimonial deleted');
      setTestimonials(t => t.filter(x => x.id !== id));
    } catch { toast.error('Failed to delete'); }
    setDeleting(null);
  };

  const handleFormSave = () => {
    setShowForm(false);
    setEditItem(null);
    fetchTestimonials();
  };

  if (authLoading) return <div className="min-h-screen pt-20 flex items-center justify-center"><div className="w-8 h-8 border-2 border-gold border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="min-h-screen pt-20 bg-[#101010]" data-testid="admin-testimonials">
      <div className="bg-[#161616] border-b border-white/[0.06] py-8 px-4 sm:px-8">
        <div className="max-w-7xl mx-auto flex items-end justify-between">
          <div>
            <p className="text-gold text-xs uppercase tracking-[0.3em] font-semibold">Admin</p>
            <h1 className="font-heading text-2xl text-white font-bold mt-1">Testimonials</h1>
          </div>
          {!showForm && (
            <Button onClick={() => { setEditItem(null); setShowForm(true); }} className="bg-gold text-white hover:bg-[#9A7B1F] rounded-lg text-xs uppercase tracking-widest gap-2" data-testid="add-testimonial-btn">
              <Plus className="w-4 h-4" /> Add Testimonial
            </Button>
          )}
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-4 sm:px-8 py-8">
        <AdminNav active="/admin/testimonials" />

        <AnimatePresence mode="wait">
          {(showForm || editItem) && (
            <div className="mb-8">
              <TestimonialForm
                testimonial={editItem}
                onSave={handleFormSave}
                onCancel={() => { setShowForm(false); setEditItem(null); }}
              />
            </div>
          )}
        </AnimatePresence>

        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-gold" /></div>
        ) : testimonials.length === 0 ? (
          <div className="text-center py-20">
            <Quote className="w-10 h-10 text-white/10 mx-auto mb-4" />
            <p className="text-white/30 text-sm">No testimonials yet</p>
            <Button onClick={() => setShowForm(true)} className="mt-4 bg-white/[0.08] text-white hover:bg-white/[0.12] rounded-lg text-xs uppercase tracking-widest gap-2">
              <Plus className="w-3.5 h-3.5" /> Add Your First Testimonial
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="testimonials-grid">
            {testimonials.map(t => (
              <motion.div
                key={t.id}
                layout
                initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
                className="bg-[#1c1c1c] border border-white/[0.06] rounded-xl p-6 flex flex-col"
                data-testid={`testimonial-card-${t.id}`}
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="flex gap-0.5">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className={`w-3 h-3 ${i < t.rating ? 'fill-gold text-gold' : 'text-white/10'}`} />
                    ))}
                  </div>
                  <div className="flex items-center gap-1">
                    {t.is_visible ? <Eye className="w-3 h-3 text-green-400/60" /> : <EyeOff className="w-3 h-3 text-red-400/60" />}
                    <span className="text-[10px] text-white/25">{t.is_visible ? 'Visible' : 'Hidden'}</span>
                  </div>
                </div>

                <p className="text-sm text-white/55 italic leading-relaxed flex-1 mb-4">"{t.quote}"</p>

                <div className="flex items-center gap-3 pt-3 border-t border-white/[0.06]">
                  <div className="w-8 h-8 rounded-full bg-gold/15 flex items-center justify-center">
                    <span className="text-gold text-xs font-bold">{t.author?.[0] || '?'}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-white truncate">{t.author}</p>
                    <p className="text-[10px] text-white/30 truncate">{t.role}</p>
                  </div>
                  <div className="flex gap-1">
                    <button onClick={() => { setEditItem(t); setShowForm(false); }} className="p-2 text-white/20 hover:text-gold transition-colors rounded-lg hover:bg-white/[0.05]" data-testid={`edit-testimonial-${t.id}`}>
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                    <button onClick={() => handleDelete(t.id)} disabled={deleting === t.id} className="p-2 text-white/20 hover:text-red-400 transition-colors rounded-lg hover:bg-white/[0.05]" data-testid={`delete-testimonial-${t.id}`}>
                      {deleting === t.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                    </button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
